import type { LevelCode, ObjectiveEvent } from '@/api/types/readiness'
import { levelNum } from '@/api/types/readiness'

// Geometry of the step sparkline (I5 §8.2, §10.2; I6 §7): one objective's
// confirmed level across a window as flat segments and steps, its
// coverage-driven drops and the level now. Computed in pixels for a fixed plot
// box, so markers are true shapes at any width (§7.2): 6px of padding top and
// bottom keeps the L3 and L0 markers inside the box.

export const isLevelMove = (e: ObjectiveEvent) => (e.event_type === 'confirmed' || e.event_type === 'declared' || e.event_type === 'overridden') && !!e.to_level

export const SPARK_PAD_X = 4
export const SPARK_PAD_Y = 6

export interface SparkSegment {
  level: LevelCode
  from: number // ms
  to: number // ms
  x1: number
  x2: number
  y: number
  current: boolean
  // The move that started this segment; null for the level at the window start.
  startEvent: ObjectiveEvent | null
}

export interface SparkStep {
  event: ObjectiveEvent
  from: LevelCode
  to: LevelCode
  x: number
  y1: number // y of `from`
  y2: number // y of `to`
  drop: boolean // coverage-driven drop (◆)
}

export interface SparkGeometry {
  segments: SparkSegment[]
  steps: SparkStep[]
  drops: SparkStep[]
  level: LevelCode
  moves: ObjectiveEvent[]
  x: (ms: number) => number
  y: (l: LevelCode) => number
  padX: number
  padY: number
}

export function stepSparklineGeometry(events: ObjectiveEvent[], code: string, from: number, to: number, W: number, H: number, startLevel: LevelCode = 'L0'): SparkGeometry {
  const padX = SPARK_PAD_X
  const padY = SPARK_PAD_Y
  const x = (ms: number) => padX + ((W - 2 * padX) * (Math.max(from, Math.min(to, ms)) - from)) / Math.max(1, to - from)
  const y = (l: LevelCode) => H - padY - ((H - 2 * padY) * levelNum(l)) / 3
  const evs = events.filter((e) => e.objective_code === code && isLevelMove(e) && new Date(e.occurred_at).getTime() <= to).sort((a, b) => a.occurred_at.localeCompare(b.occurred_at))
  let cur: LevelCode = startLevel
  const before = evs.filter((e) => new Date(e.occurred_at).getTime() < from)
  if (before.length) cur = before[before.length - 1].to_level!
  const inWindow = evs.filter((e) => new Date(e.occurred_at).getTime() >= from)
  const segments: SparkSegment[] = []
  const steps: SparkStep[] = []
  let segStart = from
  let startEvent: ObjectiveEvent | null = before[before.length - 1] ?? null
  inWindow.forEach((e) => {
    const t = new Date(e.occurred_at).getTime()
    const next = e.to_level!
    segments.push({ level: cur, from: segStart, to: t, x1: x(segStart), x2: x(t), y: y(cur), current: false, startEvent })
    steps.push({ event: e, from: cur, to: next, x: x(t), y1: y(cur), y2: y(next), drop: e.cause === 'coverage' && levelNum(next) < levelNum(cur) })
    cur = next
    segStart = t
    startEvent = e
  })
  segments.push({ level: cur, from: segStart, to, x1: x(segStart), x2: x(to), y: y(cur), current: true, startEvent })
  return { segments, steps, drops: steps.filter((s) => s.drop), level: cur, moves: inWindow, x, y, padX, padY }
}
